import { env } from "@src/env.mjs";

const serverUrl = env.NEXT_PUBLIC_SERVER_URL;

export interface AuthSession {
  access_token: string;
  refresh_token: string;
  expires_at?: number;
  user: AuthUser;
}

export interface AuthUser {
  id: string;
  email?: string;
  app_metadata: Record<string, any>;
  user_metadata: Record<string, any>;
  aud: string;
  created_at: string;
  [key: string]: any;
}

type AuthEvent = "SIGNED_IN" | "SIGNED_OUT" | "TOKEN_REFRESHED";
type AuthListener = (event: AuthEvent, session: AuthSession | null) => void;

const REFRESH_TOKEN_KEY = "ss_refresh_token";
const TOKEN_REFRESH_MARGIN_MS = 60_000; // refresh 60s before expiry

class AuthClient {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private expiresAt: number = 0;
  private user: AuthUser | null = null;
  private listeners = new Set<AuthListener>();
  private refreshPromise: Promise<void> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    if (typeof window !== "undefined") {
      this.refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    }
  }

  /**
   * Restore session from stored refresh token on app load.
   * Returns the session if valid, null otherwise.
   */
  async initialize(): Promise<AuthSession | null> {
    if (!this.refreshToken) return null;

    try {
      await this.doRefresh();
      const session = this.getSession();
      if (session) {
        this.emit("SIGNED_IN", session);
      }
      return session;
    } catch {
      this.clearTokens();
      return null;
    }
  }

  /**
   * Get the current session synchronously (if tokens are in memory).
   */
  getSession(): AuthSession | null {
    if (!this.accessToken || !this.user) return null;
    return {
      access_token: this.accessToken,
      refresh_token: this.refreshToken ?? "",
      expires_at: this.expiresAt,
      user: this.user,
    };
  }

  /**
   * Get a valid access token, auto-refreshing if expired.
   */
  async getAccessToken(): Promise<string | null> {
    if (!this.accessToken && !this.refreshToken) return null;

    if (this.isExpired()) {
      await this.doRefresh();
    }

    return this.accessToken;
  }

  /**
   * Request OTP sent to email.
   */
  async signInWithOtp(email: string): Promise<{ error?: string }> {
    const res = await fetch(`${serverUrl}/studioAuth/otp-request`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    const json = await res.json();
    if (!res.ok) return { error: json.error ?? "Failed to send OTP" };
    return {};
  }

  /**
   * Verify OTP and establish session.
   */
  async verifyOtp(
    email: string,
    token: string,
    type: "email" | "sms" = "email"
  ): Promise<{ data?: AuthSession; error?: string }> {
    const res = await fetch(`${serverUrl}/studioAuth/otp-verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, token, type }),
    });
    const json = await res.json();
    if (!res.ok) return { error: json.error ?? "OTP verification failed" };

    this.setSession(json);
    return { data: this.getSession()! };
  }

  /**
   * Sign in with email and password.
   */
  async signInWithPassword(
    email: string,
    password: string
  ): Promise<{ data?: AuthSession; error?: string }> {
    const res = await fetch(`${serverUrl}/studioAuth/password-login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const json = await res.json();
    if (!res.ok) return { error: json.error ?? "Login failed" };

    this.setSession(json);
    return { data: this.getSession()! };
  }

  /**
   * Sign out and clear all tokens.
   */
  async signOut(): Promise<void> {
    if (this.accessToken) {
      try {
        await fetch(`${serverUrl}/studioAuth/signout`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.accessToken}`,
          },
        });
      } catch {
        // Best-effort server-side sign out
      }
    }

    this.clearTokens();
    this.emit("SIGNED_OUT", null);
  }

  /**
   * Manually set session (used by impersonation, etc.)
   */
  setSession(session: {
    access_token: string;
    refresh_token: string;
    expires_at?: number;
    user?: AuthUser;
  }): void {
    this.accessToken = session.access_token;
    this.refreshToken = session.refresh_token;
    this.expiresAt = session.expires_at ?? 0;
    this.user = session.user ?? this.user;

    if (typeof window !== "undefined") {
      localStorage.setItem(REFRESH_TOKEN_KEY, session.refresh_token);
    }

    this.scheduleRefresh();
    this.emit("SIGNED_IN", this.getSession()!);
  }

  /**
   * Subscribe to auth state changes.
   */
  onAuthStateChange(
    callback: AuthListener
  ): { data: { subscription: { unsubscribe: () => void } } } {
    this.listeners.add(callback);
    return {
      data: {
        subscription: {
          unsubscribe: () => {
            this.listeners.delete(callback);
          },
        },
      },
    };
  }

  // --- Internal ---

  private isExpired(): boolean {
    if (!this.expiresAt) return true;
    return Date.now() >= this.expiresAt * 1000 - TOKEN_REFRESH_MARGIN_MS;
  }

  private async doRefresh(): Promise<void> {
    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = (async () => {
      if (!this.refreshToken) throw new Error("No refresh token");

      const res = await fetch(`${serverUrl}/studioAuth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: this.refreshToken }),
      });

      if (!res.ok) {
        this.clearTokens();
        this.emit("SIGNED_OUT", null);
        throw new Error("Token refresh failed");
      }

      const json = await res.json();
      this.accessToken = json.access_token;
      this.refreshToken = json.refresh_token ?? this.refreshToken;
      this.expiresAt = json.expires_at ?? 0;

      if (typeof window !== "undefined" && this.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, this.refreshToken);
      }

      // Fetch user info if we don't have it
      if (!this.user && this.accessToken) {
        const userRes = await fetch(`${serverUrl}/studioAuth/user`, {
          headers: { Authorization: `Bearer ${this.accessToken}` },
        });
        if (userRes.ok) {
          const userJson = await userRes.json();
          this.user = userJson.user;
        }
      }

      this.scheduleRefresh();
      this.emit("TOKEN_REFRESHED", this.getSession());
    })();

    try {
      await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private scheduleRefresh(): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    if (!this.expiresAt) return;

    const msUntilExpiry = this.expiresAt * 1000 - Date.now() - TOKEN_REFRESH_MARGIN_MS;
    if (msUntilExpiry > 0) {
      this.refreshTimer = setTimeout(() => this.doRefresh(), msUntilExpiry);
    }
  }

  private clearTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;
    this.expiresAt = 0;
    this.user = null;
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    if (typeof window !== "undefined") {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
  }

  private emit(event: AuthEvent, session: AuthSession | null): void {
    for (const listener of this.listeners) {
      try {
        listener(event, session);
      } catch {
        // Don't let listener errors break the auth flow
      }
    }
  }
}

export const authClient = new AuthClient();
